/**
 * style_boost_v1 A/B test (started 2026-09-29): does SUGGESTING Classic Film
 * Noir or Desert Mirage turn more new users into payers?
 *
 * New web users whose first video used them paid within a week at 5.2% and
 * 4.4%, against 2.6% on average (signups Aug 11–Sep 21). But most of them
 * picked the style themselves: Film Noir is the Director's pick for 0.3% of
 * songs, so the data can't tell a better style from a keener user. For half
 * of users, when either style is already one of the six style tiles a song
 * gets, it moves to the front and becomes the pre-highlighted pick. Songs that
 * don't suit them, dance projects and Lite projects are left alone.
 *
 * Assignment is FNV-1a 32-bit over "style_boost_v1:<userId>", bucket =
 * hash % 100 (the scheme in mobile/lib/experiments.ts), so a readout can
 * recompute every user's arm offline: scripts/db-read/style-boost-ab-readout.mts
 * must keep the salt and hash in sync. VITE_DIRECTOR_STYLE_BOOST_TEST=false
 * puts everyone in control.
 */

export type StyleBoostArm = "control" | "boost";

export const STYLE_BOOST_EXPERIMENT = "style_boost_v1";
export const STYLE_BOOST_TREATMENT_PERCENT = 50;
export const STYLE_BOOST_IDS: readonly string[] = [
  "film-noir",
  "desert-mirage",
];
/** The style step shows the first six suggestions as tiles. */
export const STYLE_BOOST_WINDOW = 6;
// Dance ranks for dance fit; Lite ("fast") auto-renders without a style step.
const NO_BOOST_MODES = new Set(["dance", "fast"]);

export function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash = Math.imul(hash ^ input.charCodeAt(i), 0x01000193);
  }
  return hash >>> 0;
}

export function styleBoostArmFor(
  userId: string | null | undefined,
  enabled: boolean = import.meta.env.VITE_DIRECTOR_STYLE_BOOST_TEST !== "false",
): StyleBoostArm | null {
  if (!userId) return null;
  if (!enabled) return "control";
  const bucket = fnv1a32(`${STYLE_BOOST_EXPERIMENT}:${userId}`) % 100;
  return bucket < STYLE_BOOST_TREATMENT_PERCENT ? "boost" : "control";
}

/**
 * Moves the highest-ranked boost style within the first STYLE_BOOST_WINDOW
 * suggestions to the front. Returns the ranking unchanged for control, for
 * dance and Lite projects, when a boost style already leads, or when neither
 * is in the window.
 */
export function applyStyleBoost<T extends { id: string }>(
  ranked: readonly T[],
  arm: StyleBoostArm | null | undefined,
  projectMode?: string | null,
): T[] {
  if (arm !== "boost" || NO_BOOST_MODES.has(projectMode ?? "")) {
    return [...ranked];
  }
  const windowed = ranked.slice(0, STYLE_BOOST_WINDOW);
  if (!windowed.length || STYLE_BOOST_IDS.includes(windowed[0].id)) {
    return [...ranked];
  }
  const pick = windowed.find((style) => STYLE_BOOST_IDS.includes(style.id));
  if (!pick) return [...ranked];
  return [pick, ...ranked.filter((style) => style !== pick)];
}

/** Whether the boost would change this song's pick (true in both arms). */
export function isStyleBoostEligible(
  ranked: readonly { id: string }[],
  projectMode?: string | null,
): boolean {
  if (!ranked.length) return false;
  return applyStyleBoost(ranked, "boost", projectMode)[0].id !== ranked[0].id;
}
